/**
 * @file Connexion au serveur de jeu : choix de l'adresse (meme origine / VITE_SERVER_URL / hors ligne), authentification par appareil, etat de la liaison.
 * Tout est facultatif : sans serveur joignable le jeu reste 100 % jouable hors ligne (sauvegarde locale).
 */
import { GameClient } from '@net/GameClient';
import { ServerSyncAdapter } from '@net/serverAdapters';
import { isNativeApp } from './pwa';

export const GAME_ID = 'catgunner';

export interface ServerConfig {
  /** Vide = meme origine que la page (nginx proxifie /v1). */
  baseUrl: string;
  gameId: string;
  apiKey?: string;
}

export type OnlineStatus = 'disabled' | 'connecting' | 'online' | 'offline';

/**
 * Adresse du serveur :
 *  - `?offline` dans l'URL → aucun serveur ;
 *  - `VITE_SERVER_URL` (build natif / serveur distant) avec `VITE_API_KEY` ;
 *  - build de production dans un navigateur → meme origine (le nginx du jeu proxifie et ajoute la cle d'API) ;
 *  - developpement → seulement avec `VITE_DEV_SERVER=1` (proxy Vite vers un GameServer local), sinon hors ligne.
 */
export const resolveServerConfig = (): ServerConfig | null => {
  try {
    if (new URLSearchParams(window.location.search).has('offline')) return null;
  } catch {
    /* pas d'URL exploitable : on continue */
  }
  const env = import.meta.env as Record<string, string | boolean | undefined>;
  const url = typeof env['VITE_SERVER_URL'] === 'string' ? env['VITE_SERVER_URL'].replace(/\/+$/, '') : '';
  const apiKey = typeof env['VITE_API_KEY'] === 'string' && env['VITE_API_KEY'] ? env['VITE_API_KEY'] : undefined;
  if (url) return { baseUrl: url, gameId: GAME_ID, ...(apiKey ? { apiKey } : {}) };
  if (isNativeApp()) return null;
  if (!/^https?:$/.test(window.location.protocol)) return null;
  if (env['PROD']) return { baseUrl: '', gameId: GAME_ID };
  if (env['VITE_DEV_SERVER'] === '1') return { baseUrl: '', gameId: GAME_ID };
  return null;
};

const withTimeout = <T>(p: Promise<T>, ms: number): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const t = window.setTimeout(() => reject(new Error('timeout')), ms);
    p.then(
      (v) => {
        window.clearTimeout(t);
        resolve(v);
      },
      (e: unknown) => {
        window.clearTimeout(t);
        reject(e instanceof Error ? e : new Error(String(e)));
      },
    );
  });

export class OnlineService {
  status: OnlineStatus;
  client: GameClient | null = null;
  sync: ServerSyncAdapter | null = null;
  userId: string | null = null;
  username: string | null = null;
  private listeners = new Set<(s: OnlineStatus) => void>();

  constructor(
    private readonly config: ServerConfig | null,
    private readonly deviceId: () => Promise<string>,
  ) {
    this.status = config ? 'connecting' : 'disabled';
  }

  get enabled(): boolean {
    return this.config !== null;
  }

  onStatus(fn: (s: OnlineStatus) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private setStatus(s: OnlineStatus): void {
    if (s === this.status) return;
    this.status = s;
    for (const fn of this.listeners) fn(s);
  }

  /** Authentification par appareil (cree le compte au premier passage). Retourne vrai si le serveur repond dans le delai. */
  async connect(timeoutMs = 5000): Promise<boolean> {
    if (!this.config) return false;
    this.setStatus('connecting');
    try {
      const client = this.client ?? new GameClient({ baseUrl: this.config.baseUrl, gameId: this.config.gameId, ...(this.config.apiKey ? { apiKey: this.config.apiKey } : {}) });
      const r = await withTimeout(client.authDevice(await this.deviceId(), { create: true }), timeoutMs);
      this.client = client;
      this.sync = new ServerSyncAdapter(client);
      this.userId = r.userId;
      this.username = r.username;
      this.setStatus('online');
      return true;
    } catch (err) {
      console.warn('serveur de jeu injoignable, mode hors ligne', err);
      this.setStatus('offline');
      return false;
    }
  }

  /** Nouvelle tentative silencieuse (retour au premier plan, reseau retrouve). */
  async retry(): Promise<boolean> {
    return this.status === 'online' ? true : this.connect();
  }
}
